import {
  faChevronDown,
  faChevronUp,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BareAssignmentRequestModel } from 'api/model';
import { useUserGetEmployeeAvailabilityForDateHook } from 'api/user/user';
import TextButton from 'components/inputs/TextButton';
// import Table from 'components/Table';
import GroupableTable from 'components/Table/GroupableTable';
import { ColumnSetting } from 'components/Table/utils';
import Routes from 'constants/Routes';
import useTranslations from 'contexts/basicData/useTranslations';
import useModalStack from 'contexts/modal/useModalStack';
import AvailabilityFormModal from 'pages/Employees/Availability/AvailabilityFormModal';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { makeStringComparator } from 'utils/sorting';

const Wrapper = styled.div`
  margin-bottom: 1rem;
  p {
    margin-bottom: 0.5rem;
  }
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-height: 300px;
  overflow-y: auto;
  font-size: 0.9em;
`;

const GroupHeader = styled.h3`
  margin: 0;
  font-size: 1em;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.foreground.primary};
  padding: 0.5em 0;
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-radius: 5px;
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: uppercase;
  text-decoration: underline;
`;

const StyledTextButton = styled(TextButton)`
  margin: 0;
`;

type Props = {
  requests: BareAssignmentRequestModel[];
};
const ToggleableBareAssignmentRequestsList: React.FC<Props> = ({
  requests,
}) => {
  const modalStack = useModalStack();

  const availabilityCall = useUserGetEmployeeAvailabilityForDateHook();

  const { assignmentRequestReasons } = useTranslations();
  const [showAssignments, setShowAssignments] = useState(false);

  const showAvailability = useCallback(
    async (model: BareAssignmentRequestModel) => {
      try {
        const date = model.targetStartTime;
        const userId = model.requestedForID;
        const noonDate = new Date(date.setHours(12, 0, 0, 0));
        const availability = await availabilityCall({
          userId,
          date: noonDate,
        });
        modalStack.push(
          <AvailabilityFormModal
            availability={availability}
            name={model.requestedForIDName ?? ''}
            onClose={() => modalStack.pop()}
            onSaved={() => modalStack.pop()}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            userId={userId}
            viewOnly
          />
        );
      } catch (e) {
        toast.error(
          'Det gick inte att hämta tillgängligheten. Försök igen senare.'
        );
      }
    },
    [availabilityCall, modalStack]
  );

  const renderGroupHeader = useCallback(
    (groupKey: string) => <GroupHeader>{groupKey}</GroupHeader>,
    []
  );

  const columnSettings = useMemo((): ColumnSetting<
    BareAssignmentRequestModel,
    any
  >[] => {
    const columns: ColumnSetting<BareAssignmentRequestModel, any>[] = [
      {
        head: 'Anledning',
        render: (model) =>
          assignmentRequestReasons[model.assignmentRequestReasonID],
        width: 150,
        sortFunction: makeStringComparator(
          (model) =>
            assignmentRequestReasons[model.assignmentRequestReasonID] ?? ''
        ),
      },
      {
        head: 'Fälttestare',
        render: (model) => model.requestedForIDName,
        width: 200,
        sortFunction: makeStringComparator(
          (model) => model.requestedForIDName ?? ''
        ),
      },
      {
        head: 'Länk',
        render: (model) => (
          <StyledTextButton
            onClick={() => {
              window.open(
                `${Routes.internalDeliveryGroup.requests.index}/${model.requestID}`,
                '_blank'
              );
            }}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </StyledTextButton>
        ),
        width: 50,
      },
      {
        head: 'Tillgänglighet',
        render: (model) => (
          <TextButton onClick={() => showAvailability(model)}>
            Visa tillgänglighet
          </TextButton>
        ),
        width: 150,
      },
    ];
    return columns;
  }, [assignmentRequestReasons, showAvailability]);

  return showAssignments ? (
    <Wrapper>
      <StyledTextButton onClick={() => setShowAssignments(false)}>
        Dölj <FontAwesomeIcon icon={faChevronUp} />
      </StyledTextButton>
      <GroupableTable
        groupBy={(row) => row.requestedForIDName ?? ''}
        groupHeader={renderGroupHeader}
        columnSettings={columnSettings}
        rows={requests}
      />
    </Wrapper>
  ) : (
    <StyledTextButton onClick={() => setShowAssignments(true)}>
      Visa <FontAwesomeIcon icon={faChevronDown} />
    </StyledTextButton>
  );
};

export default ToggleableBareAssignmentRequestsList;
